/**
 * V2 Embed primitive — the "Embed map" button hands out an <iframe>
 * snippet that puts a page's LIVE map on any other website (2026-07-20
 * user ask: a blogger drops their list's map into the post; a brand puts
 * its category map on their official page). The map stays current — new
 * places appear everywhere it's embedded — and every tap-through opens
 * the full journal, so embeds are doors back to the site.
 *
 * Why an iframe and not a JS widget: the Google Maps key is
 * website-restricted to yoinkadventures.com, and inside an iframe Google
 * sees the IFRAME's own URL as the referrer — the key keeps working on
 * anyone's site without loosening its restrictions.
 *
 * ?embed=1 is the whole protocol. On boot main.js strips the app chrome,
 * turns internal navigation into new-tab hops to the full site, and pins
 * an attribution bar; map-view defers the (billed) map construction
 * until the frame actually scrolls into the reader's view.
 */
import { icon } from './icons.js';

/** Is this page being viewed inside an embed frame? */
export function isEmbed() {
  return new URLSearchParams(window.location.search).get('embed') === '1';
}

/**
 * The URL the snippet frames: this page, clean, in embed mode. Filters
 * and viewport params are deliberately dropped — an embed frames itself
 * to its pins, and a stale viewport would pin it to the past.
 *
 * `params` is the exception, for a page whose IDENTITY lives in the
 * query string rather than the path (2026-09-15): a journal drilled
 * into one category is `/user/jane?category=trail`, so dropping the
 * query would hand out a frame of the whole journal instead of the
 * shelf the owner was looking at. Only pass what names the page — a
 * transient filter (region, viewport) still belongs on the floor.
 */
export function embedPageUrl(params = null) {
  const query = new URLSearchParams({ embed: '1' });
  for (const [key, value] of Object.entries(params || {})) {
    if (value) query.set(key, value);
  }
  return `${window.location.origin}${window.location.pathname}?${query}`;
}

const escapeAttr = (s) =>
  String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');

/**
 * The copy-paste snippet. loading="lazy" so a long blog post only loads
 * the frame (and Google only bills the map) once the reader scrolls
 * near it.
 */
export function embedSnippet(title, params = null) {
  return `<iframe src="${embedPageUrl(params)}" width="100%" height="480" style="border:0" loading="lazy" title="${escapeAttr(title)} — live map on Yoink Adventures"></iframe>`;
}

/**
 * Render the Embed button (event delegation: handle via handleEmbedClick).
 * `params` rides along as an encoded query string in the dataset, so the
 * click handler can rebuild it without the button owning any state.
 */
export function embedButton(title, params = null) {
  const extra = params ? String(new URLSearchParams(
    Object.entries(params).filter(([, value]) => value)
  )) : '';
  return `
    <button type="button" class="v2-btn-embed" data-embed-title="${escapeAttr(title)}"${
  extra ? ` data-embed-params="${escapeAttr(extra)}"` : ''}>
      ${icon('globe', 16)}<span>Embed map</span>
    </button>`;
}

/**
 * Handle a click that may be on an Embed button.
 * @returns {boolean} true if the click was an embed action
 */
export function handleEmbedClick(event) {
  const button = event.target.closest('[data-embed-title]');
  if (!button) return false;
  const params = button.dataset.embedParams
    ? Object.fromEntries(new URLSearchParams(button.dataset.embedParams))
    : null;
  openEmbedDialog(button.dataset.embedTitle || document.title, params);
  return true;
}

/**
 * The snippet dialog. Self-contained on document.body — pages re-render
 * their own roots freely (store noise, auth settling) and must not wipe
 * an open dialog mid-copy.
 */
export function openEmbedDialog(title, params = null) {
  document.querySelector('[data-embed-dialog]')?.remove();
  const dialog = document.createElement('dialog');
  dialog.className = 'v2-auth-dialog embed-dialog';
  dialog.setAttribute('data-embed-dialog', '');
  dialog.innerHTML = `
    <h2 class="v2-dialog-title">Put this map on your site</h2>
    <p class="log-hint">Paste this into any web page — a blog post, an official
      site, anywhere HTML goes. The map stays live: new places show up
      everywhere it’s embedded, and every pin leads back here.</p>
    <textarea data-embed-code readonly rows="4" aria-label="Embed code" spellcheck="false"></textarea>
    <button type="button" class="v2-btn-leather" data-embed-copy>
      <span data-embed-copy-label>Copy embed code</span></button>
    <button type="button" class="v2-btn-dialog-close" data-embed-close>Close</button>`;
  const code = dialog.querySelector('[data-embed-code]');
  code.value = embedSnippet(title, params);
  code.addEventListener('focus', () => code.select());
  dialog.querySelector('[data-embed-copy]').addEventListener('click', async () => {
    const label = dialog.querySelector('[data-embed-copy-label]');
    try {
      await navigator.clipboard.writeText(code.value);
      label.textContent = 'Copied — paste it into your site';
    } catch {
      // No clipboard access (older WebView): hand-select instead
      code.focus();
      label.textContent = 'Press and hold the code to copy';
    }
    setTimeout(() => {
      if (label.isConnected) label.textContent = 'Copy embed code';
    }, 2500);
  });
  dialog.querySelector('[data-embed-close]').addEventListener('click', () => dialog.close());
  dialog.addEventListener('close', () => dialog.remove());
  document.body.appendChild(dialog);
  dialog.showModal();
  return dialog;
}
